import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, CheckCircle2, XCircle, Clock, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { isFeatureEnabled } from '@/components/utils/featureFlags';

export default function ClaimsManagement() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [employerFilter, setEmployerFilter] = useState('all');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    checkFeature();
    loadData();
  }, []);

  const checkFeature = async () => {
    const enabled = await isFeatureEnabled('claims_enabled');
    setIsEnabled(enabled);
  };

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      setCurrentUser(user);
      
      // Load claims using filter API
      const claimsData = await api.entities.Claim.filter({});
      
      // Load employees
      const employeesData = await api.entities.Employee.filter({});
      
      // Load employers if admin
      let employersData = [];
      if (user.user_role === 'admin' || user.user_role === 'sub_admin') {
        employersData = await api.entities.Employer.list();
      }
      
      setClaims(claimsData || []);
      setEmployees(employeesData || []);
      setEmployers(employersData || []);
    } catch (err) {
      console.error('Failed to load claims:', err);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown';
  };

  const filteredClaims = claims.filter(claim => {
    // Status filter
    // When on "All", hide cancelled claims (they have their own tab)
    if (statusFilter === 'all' && claim.status === 'cancelled') {
      return false;
    }

    if (statusFilter !== 'all' && claim.status !== statusFilter) {
      return false;
    }
    
    // Employer filter (for admins)
    if (employerFilter !== 'all') {
      const employee = employees.find(e => e.id === claim.employee_id);
      if (!employee || employee.employer_id !== employerFilter) {
        return false;
      }
    }
    
    // Search filter
    if (searchQuery) {
      const employeeName = getEmployeeName(claim.employee_id).toLowerCase();
      const searchLower = searchQuery.toLowerCase();
      return employeeName.includes(searchLower) ||
             claim.claim_type.toLowerCase().includes(searchLower) ||
             (claim.description && claim.description.toLowerCase().includes(searchLower));
    }
    
    return true;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock },
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      denied: { color: 'bg-red-100 text-red-700', icon: XCircle },
      paid: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
      cancelled: { color: 'bg-slate-100 text-slate-600', icon: XCircle }
    };
    
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    
    return (
      <Badge className={style.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  const stats = {
    pending: claims.filter(c => c.status === 'pending').length,
    approved: claims.filter(c => c.status === 'approved').length,
    paid: claims.filter(c => c.status === 'paid').length,
    denied: claims.filter(c => c.status === 'denied').length,
    cancelled: claims.filter(c => c.status === 'cancelled').length,
    totalAmount: claims.filter(c => c.status === 'paid').reduce((sum, c) => sum + Number(c.claim_amount), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isEnabled) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md w-full">
          <CardContent className="p-12 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold">Claims Feature Disabled</h2>
            <p className="text-muted-foreground">
              The claims management feature is currently disabled by the system administrator.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Claims Management</h1>
        <p className="text-slate-500 mt-1">Review and process employee premium spending claims</p>
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${
            statusFilter === 'all'
              ? 'border-emerald-500 text-emerald-600'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          All ({claims.filter(c => c.status !== 'cancelled').length})
        </button>
        <button
          onClick={() => setStatusFilter('pending')}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${
            statusFilter === 'pending'
              ? 'border-amber-500 text-amber-600'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Pending ({stats.pending})
        </button>
        <button
          onClick={() => setStatusFilter('approved')}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${
            statusFilter === 'approved'
              ? 'border-green-500 text-green-600'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Approved ({stats.approved})
        </button>
        <button
          onClick={() => setStatusFilter('paid')}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${
            statusFilter === 'paid'
              ? 'border-emerald-500 text-emerald-600'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Paid ({stats.paid})
        </button>
        <button
          onClick={() => setStatusFilter('denied')}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${
            statusFilter === 'denied'
              ? 'border-red-500 text-red-600'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Denied ({stats.denied})
        </button>
        <button
          onClick={() => setStatusFilter('cancelled')}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${
            statusFilter === 'cancelled'
              ? 'border-slate-500 text-slate-600'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          Cancelled ({stats.cancelled})
        </button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by employee name, claim type, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {(currentUser?.user_role === 'admin' || currentUser?.user_role === 'sub_admin') && employers.length > 0 && (
              <div className="w-56">
                <Select value={employerFilter} onValueChange={setEmployerFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Employers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employers</SelectItem>
                    {employers.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredClaims.map((claim) => (
              <div 
                key={claim.id}
                className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <p className="font-medium text-slate-900">{getEmployeeName(claim.employee_id)}</p>
                    {getStatusBadge(claim.status)}
                    <Badge variant="outline">{claim.claim_type}</Badge>
                    {claim.receipt_url ? (
                      <Badge className="bg-green-50 text-green-700 border-green-200">📎 Receipt</Badge>
                    ) : (
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200">⚠️ No Receipt</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">{claim.description}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Submitted: {claim.created_at ? format(new Date(claim.created_at), 'MMM d, yyyy h:mm a') : 'N/A'}
                  </p>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xl font-bold text-slate-900">
                      ${Number(claim.claim_amount).toFixed(2)}
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/ClaimDetail/${claim.id}`)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            
            {filteredClaims.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-500">No claims found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}